import React, { useState, useRef } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Circle, Polygon } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { X, MapPin, Trash2, Save } from 'lucide-react';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function GeofenceModal({
  selectedDeviceForGeofence, geofenceName, setGeofenceName,
  onGeofenceCreated, onGeofenceEdited, onGeofenceDeleted,
  unsavedGeofence, handleSaveGeofence, setShowGeofenceModal
}) {
  const [mapCenter] = useState([28.6139, 77.2090]); // Default: Delhi
  const mapRef = useRef();

  if (!selectedDeviceForGeofence) return null;

  const handleCreated = (e) => {
    const { layerType, layer } = e;
    onGeofenceCreated(layer, layerType);
  };

  const handleEdited = (e) => {
    const layers = e.layers;
    onGeofenceEdited(layers);
  };

  const handleDeleted = (e) => {
    // Handle deletion if needed
  };

  return (
    <div className="modal-overlay" onClick={() => setShowGeofenceModal(false)}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '900px', maxHeight: '90vh' }}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            <MapPin size={24} style={{ marginRight: '8px' }} />
            Manage Geofences - {selectedDeviceForGeofence.name}
          </h2>
          <button className="modal-close" onClick={() => setShowGeofenceModal(false)}>
            <X size={24} />
          </button>
        </div>

        {/* Geofence Name Input */}
        {unsavedGeofence && (
          <div style={{ marginBottom: '16px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Geofence Name</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter geofence name"
                  value={geofenceName}
                  onChange={(e) => setGeofenceName(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn-primary"
                  onClick={handleSaveGeofence}
                  disabled={!geofenceName.trim()}
                >
                  <Save size={18} />
                  Save Geofence
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Existing Geofences List */}
        {selectedDeviceForGeofence.geofences && selectedDeviceForGeofence.geofences.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>
              Existing Geofences ({selectedDeviceForGeofence.geofences.length})
            </h3>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              maxHeight: '120px',
              overflowY: 'auto',
              padding: '8px',
              background: '#f7fafc',
              borderRadius: '8px'
            }}>
              {selectedDeviceForGeofence.geofences.map((geofence, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#2d3748' }}>
                      {geofence.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#718096', textTransform: 'capitalize' }}>
                      Type: {geofence.type}
                    </div>
                  </div>
                  <button
                    className="btn-icon"
                    onClick={() => onGeofenceDeleted(idx)}
                    style={{ color: '#e53e3e' }}
                    title="Delete Geofence"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Map with Drawing Tools */}
        <div style={{ height: '400px', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
          <MapContainer
            center={mapCenter}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <FeatureGroup>
              <EditControl
                position="topright"
                onCreated={handleCreated}
                onEdited={handleEdited}
                onDeleted={handleDeleted}
                draw={{
                  rectangle: false,
                  polyline: false,
                  marker: false,
                  circlemarker: false,
                  circle: {
                    shapeOptions: {
                      color: '#667eea',
                      fillColor: '#667eea',
                      fillOpacity: 0.2
                    }
                  },
                  polygon: {
                    shapeOptions: {
                      color: '#667eea',
                      fillColor: '#667eea',
                      fillOpacity: 0.2
                    }
                  }
                }}
              />

              {/* Display existing geofences */}
              {selectedDeviceForGeofence.geofences?.map((geofence, idx) => {
                if (geofence.type === 'circle') {
                  return (
                    <Circle
                      key={idx}
                      center={[geofence.coordinates.lat, geofence.coordinates.lng]}
                      radius={geofence.coordinates.radius}
                      pathOptions={{
                        color: '#48bb78',
                        fillColor: '#48bb78',
                        fillOpacity: 0.2
                      }}
                    />
                  );
                } else if (geofence.type === 'polygon') {
                  return (
                    <Polygon
                      key={idx}
                      positions={geofence.coordinates.map(coord => [coord.lat, coord.lng])}
                      pathOptions={{
                        color: '#48bb78',
                        fillColor: '#48bb78',
                        fillOpacity: 0.2
                      }}
                    />
                  );
                }
                return null;
              })}
            </FeatureGroup>
          </MapContainer>
        </div>

        {/* Instructions */}
        <div style={{ 
          background: '#e6f2ff', 
          border: '1px solid #667eea',
          padding: '12px', 
          borderRadius: '8px',
          fontSize: '13px',
          color: '#2d3748'
        }}>
          <strong>Instructions:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>Use the drawing tools on the top-right to create circles or polygons</li>
            <li>Give your geofence a name and click "Save Geofence"</li>
            <li>Existing geofences are shown in green</li>
            <li>Click the trash icon to delete a geofence</li>
          </ul>
        </div>

        <button
          className="btn-secondary"
          onClick={() => setShowGeofenceModal(false)}
          style={{ width: '100%', marginTop: '16px' }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default GeofenceModal;